import {
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const TYPE_COLORS = {
  booking: "blue",
  system: "purple",
  alert: "red",
  promo: "green",
  general: "default",
};

const RECIPIENT_TYPE_COLORS = {
  admin: "volcano",
  hotel: "geekblue",
  agent: "cyan",
  customer: "gold",
};

function NotificationHistory() {
  const { request } = useRequest();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    recipientType: undefined,
    isRead: undefined,
    page: 1,
    limit: 20,
  });

  const fetchAll = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", filters.page);
    params.set("limit", filters.limit);
    params.set("sort", "desc");
    if (filters.recipientType) params.set("recipientType", filters.recipientType);
    if (filters.isRead !== undefined) params.set("isRead", String(filters.isRead));

    request({
      url: `${apiPath.notificationsAll}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        if (data?.success) {
          setNotifications(data.notifications || []);
          setTotal(data.total ?? 0);
        }
      },
      onError: () => setLoading(false),
    });
  }, [filters, request]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleTableChange = (pagination) => {
    setFilters((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 180,
      render: (title, record) => (
        <div>
          <div
            style={{
              fontWeight: record.isRead ? 400 : 700,
              fontSize: 13,
              marginBottom: 2,
            }}
          >
            {title}
          </div>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {record.message}
          </Text>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 90,
      render: (type) => (
        <Tag color={TYPE_COLORS[type] || "default"}>{type || "general"}</Tag>
      ),
    },
    {
      title: "Recipient Type",
      dataIndex: "recipientType",
      key: "recipientType",
      width: 120,
      render: (rt) => (
        <Tag color={RECIPIENT_TYPE_COLORS[rt] || "default"}>{rt || "—"}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isRead",
      key: "isRead",
      width: 90,
      render: (isRead) =>
        isRead ? (
          <span style={{ color: "#52c41a", fontSize: 12 }}>
            <CheckCircleOutlined style={{ marginRight: 4 }} />
            Read
          </span>
        ) : (
          <span style={{ color: "#1890ff", fontWeight: 600, fontSize: 12 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            Unread
          </span>
        ),
    },
    {
      title: "Visibility",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status) => (
        <Tag color={status === "active" ? "success" : "default"}>
          {status || "active"}
        </Tag>
      ),
    },
    {
      title: "Sent At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => (
        <Tooltip title={moment(date).format("DD MMM YYYY, HH:mm")}>
          <span style={{ fontSize: 12 }}>{moment(date).fromNow()}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>
          Notification History
        </Title>
        <Text type="secondary">
          Overview of all notifications sent across the platform
        </Text>
      </div>

      {/* Filters */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
        <Col>
          <Select
            allowClear
            placeholder="Recipient type"
            style={{ width: 160 }}
            value={filters.recipientType}
            onChange={(v) => handleFilterChange("recipientType", v)}
          >
            <Option value="admin">Admin</Option>
            <Option value="hotel">Hotel Partner</Option>
            <Option value="agent">Agent</Option>
            <Option value="customer">Customer</Option>
          </Select>
        </Col>
        <Col>
          <Select
            allowClear
            placeholder="Read status"
            style={{ width: 140 }}
            value={filters.isRead}
            onChange={(v) => handleFilterChange("isRead", v)}
          >
            <Option value={false}>Unread</Option>
            <Option value={true}>Read</Option>
          </Select>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters({
                recipientType: undefined,
                isRead: undefined,
                page: 1,
                limit: 20,
              });
            }}
          >
            Reset
          </Button>
        </Col>
        <Col>
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Total: <strong>{total}</strong>
            </Text>
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={notifications}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: filters.page,
          pageSize: filters.limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (t, range) =>
            `${range[0]}-${range[1]} of ${t} notifications`,
        }}
        rowClassName={(record) =>
          record.isRead ? "" : "notification-row-unread"
        }
        scroll={{ x: 700 }}
        bordered
        size="middle"
      />
    </div>
  );
}

export default NotificationHistory;
